import { useState } from "react";
import {
  DailyReturn,
  Trip,
  LossEntry,
  LOSS_REASON_CODES,
  LossReasonCode,
  emptyDailyReturn,
  validateDailyReturn,
} from "@vra/schema";
import { InfoTip } from "./info-tip";

/** Field-for-field digital counterpart of the paper trip sheet (Annexure A).
 *  The ITMS reading for this period is NEVER shown here — independence of
 *  channels at the point of capture (spec §4.3-1). */
export function TripSheetForm(props: {
  depotId: string;
  operatorEntity: string;
  /** pre-filled content when re-filing a SENT_BACK return as version+1 (§6.4-1) */
  initial?: DailyReturn;
  onSubmit: (r: DailyReturn) => Promise<void>;
}) {
  const [r, setR] = useState<DailyReturn>(() =>
    props.initial ?? {
      ...emptyDailyReturn(props.depotId, props.operatorEntity),
      date: new Date().toISOString().slice(0, 10),
    });
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof DailyReturn>(k: K, v: DailyReturn[K]) =>
    setR((prev) => ({ ...prev, [k]: v }));

  const setInt = (k: keyof DailyReturn) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(k, (e.target.value === "" ? 0 : parseInt(e.target.value, 10)) as never);

  const setTrip = (i: number, patch: Partial<Trip>) =>
    setR((prev) => ({
      ...prev,
      trips: prev.trips.map((t, j) => (j === i ? { ...t, ...patch } : t)),
    }));

  const addTrip = () =>
    setR((prev) => ({
      ...prev,
      trips: [
        ...prev.trips,
        {
          seq: prev.trips.length + 1,
          from: "",
          to: "",
          arrivalTime: "",
          departureTime: "",
          soc: 0,
          tripKm: 0,
          timeKeeperAck: false,
        },
      ],
    }));

  const removeTrip = (i: number) =>
    setR((prev) => ({ ...prev, trips: prev.trips.filter((_, j) => j !== i) }));

  const setLoss = (i: number, patch: Partial<LossEntry>) =>
    setR((prev) => ({
      ...prev,
      lossEntries: prev.lossEntries.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    }));

  const checklistItems: [keyof DailyReturn["vehicleChecklist"], string][] = [
    ["brakes", "Brakes"], ["clutch", "Clutch"], ["steering", "Steering"],
    ["coolant", "Coolant"], ["wiper", "Wiper"], ["battery", "Battery"],
    ["brakeOil", "Brake oil"], ["tyrePressure", "Tyre pressure"],
    ["engineCondition", "Engine condition"], ["bodyCondition", "Body condition"],
    ["sideFront", "Side: front"], ["sideRear", "Side: rear"],
    ["sideLeft", "Side: left"], ["sideRight", "Side: right"],
  ];

  async function submit() {
    // Capture timestamp is fixed NOW, before signing and upload (§12.3).
    const finalReturn: DailyReturn = {
      ...r,
      captureTimestamp: new Date().toISOString(),
    };
    const problems = validateDailyReturn(finalReturn);
    setErrors(problems);
    if (problems.length > 0) return;
    setBusy(true);
    try {
      await props.onSubmit(finalReturn);
      setR({
        ...emptyDailyReturn(props.depotId, props.operatorEntity),
        date: new Date().toISOString().slice(0, 10),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <section className="card" data-tour="header-particulars">
        <h2>Header particulars</h2>
        <div className="grid">
          <div><label>Depot</label><input value={r.depotId} disabled /></div>
          <div><label>Operator entity</label><input value={r.operatorEntity} disabled /></div>
          <div>
            <label>Shift
              <InfoTip text="Which of the depot's two daily shifts this trip sheet covers." />
            </label>
            <select value={r.shift} onChange={(e) => set("shift", e.target.value as never)}>
              <option value="I">I</option><option value="II">II</option>
            </select>
          </div>
          <div><label>Bus registration no.
              <InfoTip text="The bus's registration plate number, e.g. DL1PC1234. This is how the depot's record is matched against the bus's own ITMS telemetry." />
            </label>
            <input value={r.busRegistrationNo} onChange={(e) => set("busRegistrationNo", e.target.value)} placeholder="DL1PC1234" /></div>
          <div><label>Route code
              <InfoTip text="The scheduled route this bus ran, e.g. R-101." />
            </label>
            <input value={r.routeCode} onChange={(e) => set("routeCode", e.target.value)} placeholder="R-101" /></div>
          <div><label>Schedule no.
              <InfoTip text="The duty schedule (timetable slot) assigned to this bus for the shift, e.g. S-17." />
            </label>
            <input value={r.scheduleNo} onChange={(e) => set("scheduleNo", e.target.value)} placeholder="S-17" /></div>
          <div><label>Date
              <InfoTip text="The calendar date this trip sheet is for. Re-filed returns keep the original date." />
            </label>
            <input type="date" value={r.date} onChange={(e) => set("date", e.target.value)} /></div>
          <div><label>Odometer OUT (km)
              <InfoTip text="Odometer reading when the bus left the depot at the start of the shift." />
            </label>
            <input type="number" inputMode="numeric" value={r.odometerOut} onChange={setInt("odometerOut")} /></div>
          <div><label>Odometer IN (km)
              <InfoTip text="Odometer reading when the bus returned to the depot at the end of the shift. Odometer IN minus OUT is the basis for actual km." />
            </label>
            <input type="number" inputMode="numeric" value={r.odometerIn} onChange={setInt("odometerIn")} /></div>
          <div><label>SOC OUT (%)
              <InfoTip text="Battery State of Charge, as a whole-number percentage (0-100), when the bus left the depot. SOC is how much charge is left in the battery, like a fuel gauge for an EV." />
            </label>
            <input type="number" inputMode="numeric" value={r.socOut} onChange={setInt("socOut")} /></div>
          <div><label>SOC IN (%)
              <InfoTip text="Battery State of Charge (0-100%) when the bus returned to the depot. SOC OUT minus SOC IN shows roughly how much charge the shift consumed." />
            </label>
            <input type="number" inputMode="numeric" value={r.socIn} onChange={setInt("socIn")} /></div>
          <div><label>Break time (min)
              <InfoTip text="Total minutes the bus was stationary on scheduled breaks during the shift (not counted as running time)." />
            </label>
            <input type="number" inputMode="numeric" value={r.breakTimeMin} onChange={setInt("breakTimeMin")} /></div>
        </div>
      </section>

      <section className="card" data-tour="crew-particulars">
        <h2>Crew particulars</h2>
        <p className="eyebrow">Identity is held off the ledger — opaque IDs only (§9.4)</p>
        <div className="grid">
          <div><label>Driver ID
              <InfoTip text="The driver's opaque officer ID (e.g. OFF-1042) — not their name. Real identities are kept off the ledger; only this reference travels with the record." />
            </label>
            <input value={r.driverRef} onChange={(e) => set("driverRef", e.target.value)} placeholder="OFF-…" /></div>
          <div><label>Conductor ID
              <InfoTip text="The conductor's opaque officer ID, same convention as Driver ID — no personal names on the ledger." />
            </label>
            <input value={r.conductorRef} onChange={(e) => set("conductorRef", e.target.value)} placeholder="OFF-…" /></div>
        </div>
      </section>

      <section className="card" data-tour="trips-table">
        <h2>Trips</h2>
        <p className="eyebrow">One row per leg — look for the "i" icons on each column if a header is unclear</p>
        <table>
          <thead>
            <tr>
              <th>#<InfoTip text="Trip sequence number for this shift — 1, 2, 3… in the order the legs were run." /></th>
              <th>From<InfoTip text="Where this leg started (stop or terminus name)." /></th>
              <th>To<InfoTip text="Where this leg ended (stop or terminus name)." /></th>
              <th>Dep<InfoTip text="Departure time for this leg, 24-hour clock (HH:MM)." /></th>
              <th>Arr<InfoTip text="Arrival time for this leg, 24-hour clock (HH:MM)." /></th>
              <th>SOC %<InfoTip text="Battery State of Charge at the end of this leg — a whole-number percentage (0-100). SOC is how much charge remains in the battery, like a fuel gauge for an EV; it should step down leg by leg as the battery is used." /></th>
              <th>Km<InfoTip text="Kilometres run on this leg." /></th>
              <th>TK ack<InfoTip text="Time Keeper acknowledgement — ticked once the depot's time keeper has confirmed this leg's departure/arrival times. Can be batched at shift end." /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {r.trips.map((t, i) => (
              <tr key={i}>
                <td className="mono">{t.seq}</td>
                <td><input value={t.from} onChange={(e) => setTrip(i, { from: e.target.value })} /></td>
                <td><input value={t.to} onChange={(e) => setTrip(i, { to: e.target.value })} /></td>
                <td><input type="time" value={t.departureTime} onChange={(e) => setTrip(i, { departureTime: e.target.value })} /></td>
                <td><input type="time" value={t.arrivalTime} onChange={(e) => setTrip(i, { arrivalTime: e.target.value })} /></td>
                <td><input type="number" inputMode="numeric" value={t.soc} onChange={(e) => setTrip(i, { soc: parseInt(e.target.value || "0", 10) })} /></td>
                <td><input type="number" inputMode="numeric" value={t.tripKm} onChange={(e) => setTrip(i, { tripKm: parseInt(e.target.value || "0", 10) })} /></td>
                <td>
                  <input type="checkbox" checked={t.timeKeeperAck}
                    onChange={(e) => setTrip(i, { timeKeeperAck: e.target.checked })} />
                </td>
                <td><button className="btn-secondary" onClick={() => removeTrip(i)}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p><button className="btn-secondary" onClick={addTrip}>Add trip</button></p>
        <p className="eyebrow">Time Keeper acknowledgement per trip; batch at shift end permitted (Annexure A)</p>
      </section>

      <section className="card" data-tour="totals">
        <h2>Totals</h2>
        <div className="grid">
          <div><label>Scheduled km
              <InfoTip text="The kilometres this bus was scheduled to run for the shift, per its duty schedule." />
            </label>
            <input type="number" inputMode="numeric" value={r.scheduledKm} onChange={setInt("scheduledKm")} /></div>
          <div><label>Actual km
              <InfoTip text="The kilometres the bus actually ran, as filed by the depot (Channel A). This is compared against the bus's own ITMS telemetry (Channel B) after submission — never shown here beforehand, to keep the two channels independent." />
            </label>
            <input type="number" inputMode="numeric" value={r.actualKm} onChange={setInt("actualKm")} /></div>
          <div><label>Loss km
              <InfoTip text="Scheduled km minus actual km — kilometres lost to breakdown, accident, diversion, cancellation, or casual contract. Enter a reason below when this is above zero." />
            </label>
            <input type="number" inputMode="numeric" value={r.lossKm} onChange={setInt("lossKm")} /></div>
        </div>
        {r.lossKm > 0 && (
          <div>
            <p className="eyebrow">Loss km by reason — casual contract is its own code (feeds Annexure 8.2 col E)</p>
            {r.lossEntries.map((l, i) => (
              <div className="grid" key={i} style={{ marginBottom: 8 }}>
                <div>
                  <label>Reason
                    <InfoTip text="Why these km were lost: breakdown, accident, diversion, cancellation, or casual contract. Casual contract is tracked separately because it feeds its own column in the monthly km statement." />
                  </label>
                  <select value={l.code} onChange={(e) => setLoss(i, { code: e.target.value as LossReasonCode })}>
                    {LOSS_REASON_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label>Km</label>
                  <input type="number" inputMode="numeric" value={l.km}
                    onChange={(e) => setLoss(i, { km: parseInt(e.target.value || "0", 10) })} />
                </div>
              </div>
            ))}
            <button className="btn-secondary"
              onClick={() => set("lossEntries", [...r.lossEntries, { code: "BREAKDOWN", km: 0 }])}>
              Add loss entry
            </button>
          </div>
        )}
      </section>

      <section className="card" data-tour="checklist">
        <h2>Pre-departure checklist
          <InfoTip text="Tick each item after physically inspecting the bus before it leaves the depot. All 14 items plus the four body sides must be checked before the bus can go out." />
        </h2>
        <div className="checklist">
          {checklistItems.map(([k, labelText]) => (
            <label key={k}>
              <input type="checkbox"
                checked={r.vehicleChecklist[k] as boolean}
                onChange={(e) => set("vehicleChecklist", { ...r.vehicleChecklist, [k]: e.target.checked })} />
              {labelText}
            </label>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <label>Remarks
            <InfoTip text="Free-text notes on anything found during the checklist inspection, e.g. a minor issue noted but not blocking departure." />
          </label>
          <input value={r.vehicleChecklist.remarks}
            onChange={(e) => set("vehicleChecklist", { ...r.vehicleChecklist, remarks: e.target.value })} />
        </div>
      </section>

      <section className="card" data-tour="memo-duty">
        <h2>Memo &amp; duty</h2>
        <div className="grid">
          <div><label>Memo given
              <InfoTip text="Time the depot handed the duty memo to the crew before departure." />
            </label>
            <input type="time" value={r.memoGivenTime} onChange={(e) => set("memoGivenTime", e.target.value)} /></div>
          <div><label>Memo taken
              <InfoTip text="Time the crew handed the memo back on return to the depot." />
            </label>
            <input type="time" value={r.memoTakenTime} onChange={(e) => set("memoTakenTime", e.target.value)} /></div>
          <div>
            <label>Duty status
              <InfoTip text="Whether the crew completed the duty as scheduled (OK DUTY) or something prevented that (NOT OK DUTY)." />
            </label>
            <select value={r.dutyStatus} onChange={(e) => set("dutyStatus", e.target.value as never)}>
              <option value="OK_DUTY">OK DUTY</option>
              <option value="NOT_OK_DUTY">NOT OK DUTY</option>
            </select>
          </div>
        </div>
      </section>

      {errors.length > 0 && (
        <ul className="errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
      )}

      <div className="actions" style={{ margin: "16px 0 48px" }} data-tour="submit-button">
        <button className="btn-primary" onClick={submit} disabled={busy}>
          {busy ? "Signing…" : "Sign & queue return"}
        </button>
      </div>
    </div>
  );
}
